import { reconhecerColunas, simplificarTitulo, type CamposDoCabecalho } from '../imports/cabecalho';
import { CABECALHO_INDICES, type AbaDaPlanilha } from './planilha-de-indices';
import { CABECALHO_POR_PECA } from './planilha-por-peca';
import { CABECALHOS as CABECALHOS_DA_NUMERADA } from './service-sheet';

/**
 * O mapeamento manual das colunas (22/10/2026): quando o reconhecedor não acha o cabeçalho de
 * nenhum dos três formatos pelo nome, a pessoa diz qual coluna é cada campo.
 *
 * O reconhecedor (`imports/cabecalho.ts`) já entende acento, caixa, abreviação e palavra a mais
 * — "Qtd.", "Cód. compras", "Nome da peça". O que ele não adivinha é o título que ninguém
 * previu ("Elemento estrutural", "Insumo/serviço", uma planilha em outra língua). Antes disso a
 * tela só dizia o que faltava, e a pessoa tinha de renomear as colunas no Excel e tentar de
 * novo. Agora ela escolhe as colunas ali mesmo, com o que o reconhecedor achou já preenchido.
 *
 * O mapeamento **não cria um quarto leitor**: ele reescreve a linha dos títulos da aba com o
 * título que o leitor do formato reconhece pelo nome inteiro, e a aba segue pelo caminho de
 * sempre — a mesma leitura, os mesmos avisos, a mesma prévia. Os testes conferem a ida e volta
 * de cada formato, porque é ela que garante que o título reescrito cai na coluna escolhida.
 */

export type FormatoDaPlanilha = 'POR_PECA' | 'PLANA' | 'NUMERADA';

export interface CampoParaMapear {
  /** O nome do campo no leitor do formato. */
  readonly campo: string;
  /** Como a tela o chama. */
  readonly rotulo: string;
  readonly obrigatorio: boolean;
  /** O título que o leitor do formato reconhece pelo nome inteiro — entra no lugar do escrito. */
  readonly titulo: string;
}

export interface FormatoParaMapear {
  readonly formato: FormatoDaPlanilha;
  readonly rotulo: string;
  /** Uma frase sobre o que a planilha deste formato traz, para quem escolhe. */
  readonly descricao: string;
  readonly campos: readonly CampoParaMapear[];
}

/** A coluna de cada campo; `-1` é "a planilha não tem". */
export type ColunasMapeadas = Readonly<Record<string, number>>;

const SINONIMOS: Readonly<Record<FormatoDaPlanilha, CamposDoCabecalho<string>>> = {
  POR_PECA: CABECALHO_POR_PECA,
  PLANA: CABECALHO_INDICES,
  NUMERADA: CABECALHOS_DA_NUMERADA,
};

function campo(nome: string, rotulo: string, titulo: string, obrigatorio = false): CampoParaMapear {
  return { campo: nome, rotulo, titulo, obrigatorio };
}

/** Os três formatos, na ordem em que a detecção os pergunta: por peça, índices, numerada. */
export const FORMATOS_PARA_MAPEAR: readonly FormatoParaMapear[] = [
  {
    formato: 'POR_PECA',
    rotulo: 'Por peça',
    descricao:
      'Uma linha por peça × serviço × material, com a quantidade pronta ("B24 leva 33,18 kg de aço").',
    campos: [
      campo('disciplina', 'Disciplina', 'disciplina', true),
      campo('servico', 'Serviço', 'servico', true),
      campo('peca', 'Nome da peça', 'nome', true),
      campo('material', 'Material', 'material', true),
      campo('quantidade', 'Quantidade', 'quantidade', true),
      campo('andar', 'Andar ou pavimento', 'andar'),
      campo('unidade', 'Unidade', 'unidade'),
      campo('classe', 'Classe (material, mão de obra…)', 'classe'),
      campo('codigo', 'Código de compras', 'codigo'),
    ],
  },
  {
    formato: 'PLANA',
    rotulo: 'Índices',
    descricao: 'Disciplina, serviço e item, com o índice de consumo e a perda de cada item.',
    campos: [
      campo('disciplina', 'Disciplina', 'disciplina', true),
      campo('servico', 'Serviço', 'servico', true),
      campo('item', 'Item', 'item', true),
      campo('etapa', 'Etapa', 'etapa'),
      campo('unidadeDoServico', 'Unidade do serviço', 'unidade do servico'),
      campo('medePor', 'Mede por', 'mede por'),
      campo('codigo', 'Código de compras', 'codigo'),
      campo('tipo', 'Tipo do item', 'tipo'),
      campo('unidadeDoItem', 'Unidade do item', 'unidade do item'),
      campo('indice', 'Índice de consumo', 'indice'),
      campo('perda', 'Perda (%)', 'perda'),
    ],
  },
  {
    formato: 'NUMERADA',
    rotulo: 'Numerada do orçamento',
    descricao:
      'A numeração (3, 3.1, 3.1.1) monta disciplina, serviço e item; a descrição dá o nome de cada um.',
    campos: [
      campo('item', 'Item (a numeração)', 'item', true),
      campo('description', 'Descrição', 'descricao', true),
      campo('unit', 'Unidade', 'unidade'),
      campo('quantity', 'Quantidade', 'quantidade'),
      campo('code', 'Código de compras', 'codigo'),
      campo('payment', 'Pagamento', 'pagamento'),
    ],
  },
];

export function formatoParaMapear(formato: FormatoDaPlanilha): FormatoParaMapear {
  return FORMATOS_PARA_MAPEAR.find((candidato) => candidato.formato === formato)!;
}

/** Quantas linhas do topo a tela oferece como "linha dos títulos". */
export const LINHAS_OFERECIDAS_PARA_TITULO = 20;

/** `0` → `"A"`, `27` → `"AB"` — como o Excel chama a coluna. */
export function letraDaColuna(indice: number): string {
  let nome = '';
  let restante = indice + 1;
  while (restante > 0) {
    const resto = (restante - 1) % 26;
    nome = String.fromCharCode(65 + resto) + nome;
    restante = Math.floor((restante - 1) / 26);
  }
  return nome;
}

function ehNumero(texto: string): boolean {
  const limpo = texto.trim().replace(/\s/g, '');
  if (limpo === '') return false;
  const normalizado =
    limpo.includes(',') && limpo.lastIndexOf(',') > limpo.lastIndexOf('.')
      ? limpo.replace(/\./g, '').replace(',', '.')
      : limpo.replace(/,/g, '');
  return Number.isFinite(Number(normalizado));
}

/**
 * A linha que mais parece a dos títulos: a de mais células com texto (número não conta) entre as
 * primeiras do topo. Título da obra e linha em branco perdem; no empate, vale a de cima.
 */
export function linhaProvavelDoCabecalho(rows: readonly (readonly string[])[]): number {
  let melhor = 0;
  let maisTextos = -1;
  for (let linha = 0; linha < Math.min(rows.length, LINHAS_OFERECIDAS_PARA_TITULO); linha += 1) {
    const textos = rows[linha]!.filter(
      (celula) => celula.trim() !== '' && !ehNumero(celula),
    ).length;
    if (textos > maisTextos) {
      melhor = linha;
      maisTextos = textos;
    }
  }
  return melhor;
}

/** Quantas linhas abaixo dos títulos o palpite pelo conteúdo olha. */
const AMOSTRA = 50;

function amostraDaColuna(
  rows: readonly (readonly string[])[],
  linha: number,
  coluna: number,
): string[] {
  const valores: string[] = [];
  for (let posicao = linha + 1; posicao < rows.length && valores.length < AMOSTRA; posicao += 1) {
    const valor = (rows[posicao]![coluna] ?? '').trim();
    if (valor !== '') valores.push(valor);
  }
  return valores;
}

/** A numeração do orçamento: `3`, `3.1`, `3.1.1`, com o ponto final que às vezes sobra. */
const NUMERACAO = /^\d+(\.\d+)*\.?$/;

/**
 * O palpite pelo **conteúdo**, para o campo obrigatório que o título não entregou: a coluna de
 * números é a quantidade da planilha por peça (é o único número dela); a de "3.1.1" é o item da
 * numerada. Só entre as colunas que nenhum campo pegou, e só com a coluna inteira concordando —
 * a pessoa ainda confere antes de ler.
 */
const PALPITE_PELO_CONTEUDO: Readonly<
  Partial<Record<FormatoDaPlanilha, Readonly<Record<string, (valores: string[]) => boolean>>>>
> = {
  POR_PECA: {
    quantidade: (valores) => valores.length > 0 && valores.every(ehNumero),
  },
  NUMERADA: {
    item: (valores) =>
      valores.length > 0 &&
      valores.every((valor) => NUMERACAO.test(valor)) &&
      valores.some((valor) => valor.replace(/\.$/, '').includes('.')),
  },
};

/**
 * O que dá para sugerir antes de a pessoa escolher: a coluna de cada campo pelo título
 * (o reconhecedor comum, com os sinônimos do formato) e, para o que faltou, pelo conteúdo.
 */
export function sugerirColunas(
  formato: FormatoDaPlanilha,
  rows: readonly (readonly string[])[],
  linha: number,
): ColunasMapeadas {
  const titulos = rows[linha] ?? [];
  const achadas = reconhecerColunas(SINONIMOS[formato], titulos) as Readonly<
    Record<string, number>
  >;
  const colunas: Record<string, number> = {};
  for (const { campo: nome } of formatoParaMapear(formato).campos) {
    colunas[nome] = achadas[nome] ?? -1;
  }

  const usadas = new Set(Object.values(colunas).filter((coluna) => coluna >= 0));
  const largura = Math.max(0, ...rows.slice(linha, linha + AMOSTRA + 1).map((row) => row.length));
  for (const [nome, combina] of Object.entries(PALPITE_PELO_CONTEUDO[formato] ?? {})) {
    if ((colunas[nome] ?? -1) >= 0) continue;
    const candidatas: number[] = [];
    for (let coluna = 0; coluna < largura; coluna += 1) {
      if (!usadas.has(coluna) && combina(amostraDaColuna(rows, linha, coluna))) {
        candidatas.push(coluna);
      }
    }
    // Duas colunas concordando é dúvida, e dúvida a pessoa resolve.
    if (candidatas.length === 1) {
      colunas[nome] = candidatas[0]!;
      usadas.add(candidatas[0]!);
    }
  }
  return colunas;
}

/**
 * O formato mais provável para a linha dos títulos: o que reconhece **mais colunas**; no empate,
 * o que tem a maior parte dos obrigatórios; e, por fim, a ordem da detecção — por peça, índices,
 * numerada.
 *
 * Contar só a parte dos obrigatórios errava a planilha por peça com dois títulos estranhos: três
 * de cinco (60%) perdia para os dois de três da de índices (Disciplina e Serviço, 67%), embora a
 * por peça reconhecesse também o andar, a quantidade e a unidade.
 */
export function formatoProvavel(
  rows: readonly (readonly string[])[],
  linha: number,
): FormatoDaPlanilha {
  let melhor: FormatoDaPlanilha = 'POR_PECA';
  let melhorNota: readonly [number, number] = [-1, -1];
  for (const { formato, campos } of FORMATOS_PARA_MAPEAR) {
    const colunas = sugerirColunas(formato, rows, linha);
    const achado = (c: CampoParaMapear): boolean => (colunas[c.campo] ?? -1) >= 0;
    const obrigatorios = campos.filter((c) => c.obrigatorio);
    const nota = [
      campos.filter(achado).length,
      obrigatorios.filter(achado).length / obrigatorios.length,
    ] as const;
    if (nota[0] > melhorNota[0] || (nota[0] === melhorNota[0] && nota[1] > melhorNota[1])) {
      melhor = formato;
      melhorNota = nota;
    }
  }
  return melhor;
}

function emLista(nomes: readonly string[]): string {
  if (nomes.length <= 1) return nomes[0] ?? '';
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]!}`;
}

/** O que impede a leitura com estas colunas; `undefined` é "pode ler". */
export function problemaDoMapeamento(
  formato: FormatoDaPlanilha,
  colunas: ColunasMapeadas,
): string | undefined {
  const { campos } = formatoParaMapear(formato);
  const faltam = campos
    .filter((c) => c.obrigatorio && (colunas[c.campo] ?? -1) < 0)
    .map((c) => c.rotulo);
  if (faltam.length > 0) return `Falta dizer a coluna de ${emLista(faltam)}.`;

  const porColuna = new Map<number, string[]>();
  for (const c of campos) {
    const coluna = colunas[c.campo] ?? -1;
    if (coluna < 0) continue;
    porColuna.set(coluna, [...(porColuna.get(coluna) ?? []), c.rotulo]);
  }
  for (const [coluna, rotulos] of porColuna) {
    if (rotulos.length > 1) {
      return `A coluna ${letraDaColuna(coluna)} está em ${emLista(rotulos)}. Cada coluna serve a um campo só.`;
    }
  }
  return undefined;
}

/** Os títulos da linha, comparáveis: simplificados, sem as células vazias do fim. */
function assinatura(rows: readonly (readonly string[])[], linha: number): string {
  const titulos = (rows[linha] ?? []).map((celula) => simplificarTitulo(celula));
  while (titulos.length > 0 && titulos[titulos.length - 1] === '') titulos.pop();
  return titulos.join('|');
}

/**
 * As abas prontas para o leitor do formato: a escolhida e as que têm **os mesmos títulos na
 * mesma linha** (a numerada traz uma aba por tipologia, todas iguais — mapear uma vale para
 * as irmãs). A linha dos títulos vira a do formato, com o título reconhecido em cada coluna
 * escolhida; as linhas acima ficam vazias, para nenhuma delas passar por cabeçalho, e a
 * posição de cada linha não muda — o aviso continua apontando a linha certa.
 */
export function aplicarMapeamento(
  abas: readonly AbaDaPlanilha[],
  nomeDaAba: string,
  linha: number,
  formato: FormatoDaPlanilha,
  colunas: ColunasMapeadas,
): readonly AbaDaPlanilha[] {
  const escolhida = abas.find((aba) => aba.name === nomeDaAba);
  if (escolhida === undefined) return [];

  const { campos } = formatoParaMapear(formato);
  const largura = Math.max(0, ...campos.map((c) => (colunas[c.campo] ?? -1) + 1));
  const titulos: string[] = Array.from({ length: largura }, () => '');
  for (const c of campos) {
    const coluna = colunas[c.campo] ?? -1;
    if (coluna >= 0) titulos[coluna] = c.titulo;
  }

  const alvo = assinatura(escolhida.rows, linha);
  return abas
    .filter((aba) => aba === escolhida || (alvo !== '' && assinatura(aba.rows, linha) === alvo))
    .map((aba) => ({
      name: aba.name,
      rows: aba.rows.map((row, posicao) =>
        posicao < linha ? [] : posicao === linha ? titulos : row,
      ),
    }));
}
